import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, Building2, Sparkles, Loader2, Home, FileCheck } from 'lucide-react'
import hmrcBadge from '../assets/hmrc.jpeg'
import pricingBackground from '../assets/pricing-background.svg'
import PricingCard from './PricingCard'
import type { PricingPlan } from '../types'
import paymentService from '../utils/paymentService'

/**
 * Product tabs that are still being built. A tab listed here shows a
 * "coming soon" panel INSTEAD of its plan cards, so anything priced and on sale
 * must not appear here — Self Assessment and Confirmation Statement are both
 * live, so the list is empty.
 */
const IN_PROGRESS_TABS: string[] = []

const PricingSection: React.FC = () => {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<Record<string, PricingPlan[]>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string>('')

  useEffect(() => {
    loadPricingPlans()
  }, [])

  const loadPricingPlans = async () => {
    try {
      setIsLoading(true)
      const plansData = await paymentService.listPricingPlans()
      
      // plansData is already the correct structure: { "self-assessment": [...], "ct600": [...] }
      const groupedPlans = plansData as unknown as Record<string, PricingPlan[]>
      
      // Sort plans within each category by sortOrder
      const sortedGroupedPlans: Record<string, PricingPlan[]> = {}
      Object.keys(groupedPlans).forEach(key => {
        const plansForKey = groupedPlans[key]
        if (plansForKey) {
          sortedGroupedPlans[key] = plansForKey.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        }
      })
      
      // Preserve confirmation-statement items if they already exist from API
      const existingConfirmationStatement = sortedGroupedPlans['confirmation-statement'] || []
      
      // Initialize company-accounts and confirmation-statement arrays
      const companyAccountsItems: PricingPlan[] = []
      let confirmationStatementItems: PricingPlan[] = []
      
      // Step 1: Extract "Unlimited CT600" items from confirmation-statement and move back to ct600
      if (existingConfirmationStatement.length > 0) {
        const unlimitedCT600Items = existingConfirmationStatement.filter(plan => {
          const name = (plan.name || '').toLowerCase()
          return name.includes('unlimited ct600') || (name.includes('unlimited') && name.includes('ct600'))
        })
        
        if (unlimitedCT600Items.length > 0) {
          // Move back to ct600
          if (!sortedGroupedPlans['ct600']) {
            sortedGroupedPlans['ct600'] = []
          }
          sortedGroupedPlans['ct600'] = [...sortedGroupedPlans['ct600'], ...unlimitedCT600Items]
          // Remove from confirmation-statement (only remove Unlimited CT600, not all unlimited items)
          confirmationStatementItems = existingConfirmationStatement.filter(plan => {
            const name = (plan.name || '').toLowerCase()
            return !name.includes('unlimited ct600') && !(name.includes('unlimited') && name.includes('ct600'))
          })
        } else {
          confirmationStatementItems = existingConfirmationStatement
        }
      }
      
      // Step 2: Dormant company plans are returned under productType "ct600",
      // so they stay in the Corporation Tax tab alongside the other CT600 plans
      // rather than being split into a hidden company-accounts bucket.

      // Step 3: Extract "Unlimited CT600" items from ct600 that might need to stay in ct600
      // We want to keep all CT600 items (including Unlimited) in Corporation Tax, but move dormant ones out
      // So no need to filter out unlimited items from ct600
      
      // Step 4: If ct600 has more than needed items after moving dormant, keep all CT600-related items
      // (Unlimited CT600 should stay with Corporation Tax)
      
      // Step 5: Check if any ct600 items should be moved to confirmation-statement based on their names
      // (Only confirmation-related items, not CT600 items)
      if (sortedGroupedPlans['ct600']) {
        const confirmationItems = sortedGroupedPlans['ct600'].filter(plan => {
          const name = (plan.name || '').toLowerCase()
          const slug = (plan.slug || '').toLowerCase()
          // Don't move CT600 items (including Unlimited CT600) - they belong in Corporation Tax
          const isCT600Item = name.includes('ct600') || name.includes('corporation tax')
          const isUnlimitedCT600 = name.includes('unlimited ct600') || (name.includes('unlimited') && name.includes('ct600'))
          const isConfirmation = name.includes('confirmation') || 
                                name.includes('confirmation statement') ||
                                slug.includes('confirmation')
          
          // Only move items that are confirmation-related AND not CT600-related (including Unlimited CT600)
          return isConfirmation && !isCT600Item && !isUnlimitedCT600
        })
        
        if (confirmationItems.length > 0) {
          confirmationStatementItems = [...confirmationStatementItems, ...confirmationItems]
          // Remove from ct600
          sortedGroupedPlans['ct600'] = sortedGroupedPlans['ct600'].filter(plan => {
            const name = (plan.name || '').toLowerCase()
            const slug = (plan.slug || '').toLowerCase()
            const isCT600Item = name.includes('ct600') || name.includes('corporation tax')
            const isUnlimited = name.includes('unlimited')
            const isConfirmation = name.includes('confirmation') || 
                                  name.includes('confirmation statement') ||
                                  slug.includes('confirmation')
            
            return !(isConfirmation && !isCT600Item && !isUnlimited)
          })
        }
      }
      
      // Step 6: Move whatever is currently in company-accounts to confirmation-statement
      // (This handles the case where items were previously assigned to company-accounts)
      if (sortedGroupedPlans['company-accounts'] && sortedGroupedPlans['company-accounts'].length > 0) {
        // Filter out any "Unlimited CT600" items before moving to confirmation-statement
        const itemsToMove = sortedGroupedPlans['company-accounts'].filter(plan => {
          const name = (plan.name || '').toLowerCase()
          return !name.includes('unlimited ct600') && !name.includes('unlimited')
        })
        confirmationStatementItems = [...confirmationStatementItems, ...itemsToMove]
        
        // Move any Unlimited CT600 items from company-accounts back to ct600
        const unlimitedFromCompanyAccounts = sortedGroupedPlans['company-accounts'].filter(plan => {
          const name = (plan.name || '').toLowerCase()
          return name.includes('unlimited ct600') || name.includes('unlimited')
        })
        
        if (unlimitedFromCompanyAccounts.length > 0) {
          if (!sortedGroupedPlans['ct600']) {
            sortedGroupedPlans['ct600'] = []
          }
          sortedGroupedPlans['ct600'] = [...sortedGroupedPlans['ct600'], ...unlimitedFromCompanyAccounts]
        }
      }
      
      // Step 7: Final cleanup - Remove "Unlimited CT600" items from confirmation-statement if they exist
      const unlimitedCT600InConfirmation = confirmationStatementItems.filter(plan => {
        const name = (plan.name || '').toLowerCase()
        return name.includes('unlimited ct600') || (name.includes('unlimited') && name.includes('ct600'))
      })
      
      if (unlimitedCT600InConfirmation.length > 0) {
        // Move them back to ct600
        if (!sortedGroupedPlans['ct600']) {
          sortedGroupedPlans['ct600'] = []
        }
        sortedGroupedPlans['ct600'] = [...sortedGroupedPlans['ct600'], ...unlimitedCT600InConfirmation]
        // Remove from confirmation-statement
        confirmationStatementItems = confirmationStatementItems.filter(plan => {
          const name = (plan.name || '').toLowerCase()
          return !name.includes('unlimited ct600') && !(name.includes('unlimited') && name.includes('ct600'))
        })
      }
      
      // Step 9: Set the final arrays
      sortedGroupedPlans['confirmation-statement'] = confirmationStatementItems.length > 0 
        ? confirmationStatementItems 
        : []
      
      sortedGroupedPlans['company-accounts'] = companyAccountsItems
      
      setPlans(sortedGroupedPlans)

      // Default to Corporation Tax (the live tab) when available
      if (sortedGroupedPlans['ct600']) {
        setActiveTab('ct600')
      } else {
        const firstProductType = Object.keys(sortedGroupedPlans).find(
          key => key !== 'company-accounts'
        )
        if (firstProductType) {
          setActiveTab(firstProductType)
        }
      }
    } catch (err) {
      console.error('Failed to load pricing plans:', err)
      setError('Unable to load pricing plans')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGetStarted = () => {
    navigate('/sign-up')
  }

  const formatCurrency = (amount: number, currency: string = 'GBP'): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const getTabIcon = (productType: string) => {
    switch (productType) {
      case 'self-assessment':
        return FileText
      case 'ct600':
        return Building2
      case 'confirmation-statement':
        return Home
      case 'company-accounts':
        return FileCheck
      default:
        return Sparkles
    }
  }

  const getTabLabel = (productType: string) => {
    switch (productType) {
      case 'self-assessment':
        return 'Self Assessment'
      case 'ct600':
        return 'Corporation Tax & Company Accounts'
      case 'confirmation-statement':
        return 'Confirmation Statement'
      case 'company-accounts':
        return 'Company Accounts'
      case 'dormant-accounts':
        return 'Dormant Accounts'
      default:
        return productType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }
  }


  const mapPlanToCardProps = (plan: PricingPlan, index: number, allPlans: PricingPlan[]) => {
    // Extract clean plan name - remove product type prefix if present
    const planName = plan.name || ''
    // Try to extract just the tier name (Basic, Standard, Premium, etc.)
    const planTypeMatch = planName.match(/(?:-|–|—)\s*([A-Za-z]+(?:\s+[A-Za-z]+)?)$|([A-Za-z]+(?:\s+[A-Za-z]+)?)$/)
    const planType = planTypeMatch ? (planTypeMatch[1] || planTypeMatch[2]) : planName.split('-').pop()?.trim() || planName
    
    // Mark second plan as popular only for self-assessment tab, use same design for all other tabs
    const isPopular = activeTab === 'self-assessment' && index === 1 && allPlans.length > 1

    // Custom-pricing plans (e.g. the Accountant & Multi-Company Package) show a
    // "Contact us" CTA linking out instead of a price + checkout button.
    const metadata = (plan.metadata || {}) as {
      customPricing?: boolean
      description?: string
      ctaLabel?: string
      ctaUrl?: string
    }
    const isCustomPricing = !!metadata.customPricing

    return {
      planType: isCustomPricing ? planName : planType,
      price: formatCurrency(plan.price, plan.currency),
      period: plan.billingCycle === 'one-off' ? 'one-time' : plan.billingCycle,
      features: plan.features.map(feature => ({ text: feature, included: true })),
      buttonText: isCustomPricing ? 'Get in touch' : 'Get Started',
      isPopular: isCustomPricing ? false : isPopular,
      icon: null,
      customPricing: isCustomPricing,
      description: metadata.description,
      ctaUrl: metadata.ctaUrl,
    }
  }

  return (
    <section className='bg-white py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-20 mx-auto w-full overflow-hidden'>
      <div
        className='mx-auto rounded-[20px] sm:rounded-[24px] lg:rounded-[32px] p-4 sm:p-6 md:p-10 lg:p-20 bg-[#181D27] w-full'
        style={{
          background: `linear-gradient(135deg, #181D27 0%, #0E141F 100%), url(${pricingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
        }}
      >
        {/* Header */}
        <div className='text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16'>
          <h2 className='text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3 md:mb-4 px-2 sm:px-0'>
            Flexible Pricing Plan
          </h2>
          <p className='text-gray-300 text-xs sm:text-sm md:text-base lg:text-lg max-w-3xl mx-auto leading-relaxed px-2 sm:px-4 md:px-0'>
            Choose a plan that fits your needs — no hidden fees, no surprises.
            Whether you&apos;re a freelancer, small business, or accountant, our
            flexible pricing ensures you only pay for what you need.
          </p>
          <div className='mt-4 sm:mt-6 flex justify-center'>
            <div className='inline-flex items-center gap-3 rounded-full bg-white/95 px-4 py-2 shadow-sm'>
              <img
                src={hmrcBadge}
                alt='HMRC Recognised for Corporation Tax Filing'
                className='h-10 w-auto'
              />
              <span className='text-xs sm:text-sm font-semibold text-[#181D27]'>
                HMRC Recognised
              </span>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='h-8 w-8 animate-spin text-white' />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className='text-center py-12'>
            <p className='text-red-400 mb-4'>{error}</p>
            <button
              onClick={loadPricingPlans}
              className='text-white hover:text-gray-300 underline'
            >
              Try Again
            </button>
          </div>
        )}

        {/* Tabs and Pricing Cards */}
        {!isLoading && !error && Object.keys(plans).length > 0 && (
          <>
            {/* Tabs */}
            <div className='mb-6 sm:mb-8 flex justify-center'>
              <div className='flex justify-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide pb-2'>
                {(() => {
                  const tabOrder = ['ct600', 'self-assessment', 'confirmation-statement']
                  const orderedTabs = [...new Set([...tabOrder, ...Object.keys(plans)])].filter(
                    tab => plans[tab] && tab !== 'company-accounts'
                  )

                  return orderedTabs.map((productType) => {
                    const Icon = getTabIcon(productType)
                    const isActive = activeTab === productType
                    const isInProgress = IN_PROGRESS_TABS.includes(productType)

                    return (
                      <button
                        key={productType}
                        onClick={() => setActiveTab(productType)}
                        className={`flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base whitespace-nowrap flex-shrink-0 ${
                          isActive
                            ? 'bg-white text-gray-900 shadow-lg'
                            : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                        }`}
                      >
                        <Icon className='h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0' />
                        <span>{getTabLabel(productType)}</span>
                        {isInProgress && (
                          <span
                            className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                              isActive
                                ? 'bg-amber-100 text-amber-700 border-amber-300'
                                : 'bg-amber-500/15 text-amber-300 border-amber-400/40'
                            }`}
                          >
                            In Progress
                          </span>
                        )}
                      </button>
                    )
                  })
                })()}
              </div>
            </div>

            {/* Pricing Cards - Centered based on tabs position */}
            {activeTab && IN_PROGRESS_TABS.includes(activeTab) ? (
              <div className='flex justify-center px-4'>
                <div className='max-w-xl w-full text-center rounded-2xl border border-amber-400/30 bg-amber-500/10 px-6 py-10 sm:px-10 sm:py-12'>
                  <span className='inline-flex items-center gap-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/20 border border-amber-400/40 rounded-full px-3 py-1 mb-4'>
                    In Progress
                  </span>
                  <h3 className='text-xl sm:text-2xl font-semibold text-white mb-2'>
                    {getTabLabel(activeTab)} is coming soon
                  </h3>
                  <p className='text-gray-300 text-sm sm:text-base leading-relaxed'>
                    We&apos;re actively building this. In the meantime, you can use BritFiler to file your Corporation Tax &amp; Company Accounts today.
                  </p>
                </div>
              </div>
            ) : activeTab && plans[activeTab] && plans[activeTab].length > 0 ? (
              <div className='w-full flex justify-center'>
                <div className='flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-5 items-stretch max-w-full'>
                  {plans[activeTab]?.map((plan, index) => {
                    const cardProps = mapPlanToCardProps(plan, index, plans[activeTab] || [])
                    return (
                      <div
                        key={plan._id}
                        className='w-full sm:w-auto max-w-[320px] flex-shrink-0'
                      >
                        <PricingCard
                          planType={cardProps.planType || 'Standard'}
                          price={cardProps.price}
                          period={cardProps.period}
                          features={cardProps.features}
                          buttonText={cardProps.buttonText}
                          isPopular={cardProps.isPopular}
                          icon={cardProps.icon}
                          customPricing={cardProps.customPricing}
                          description={cardProps.description}
                          ctaUrl={cardProps.ctaUrl}
                          onButtonClick={handleGetStarted}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : activeTab && plans[activeTab] && plans[activeTab].length === 0 ? (
              <div className='flex justify-center items-center py-12'>
                <p className='text-gray-400 text-lg'>No plans available for this category.</p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}

export default PricingSection
